import { useRef, useState, useCallback, useEffect } from "react";

// Instagram-style presets. Each is a canvas 2D `filter` string — cheap
// (native to the canvas API, GPU-composited in modern browsers) and
// enough to give a broadcast a distinct look without per-pixel work.
export const FILTER_PRESETS = [
  { id: "none", label: "Original", css: "none", swatch: "linear-gradient(135deg,#e5e7eb,#9ca3af)" },
  { id: "clarendon", label: "Clarendon", css: "contrast(1.2) saturate(1.35) brightness(1.05)", swatch: "linear-gradient(135deg,#38bdf8,#0ea5e9)" },
  { id: "gingham", label: "Gingham", css: "brightness(1.05) sepia(0.15) contrast(0.9)", swatch: "linear-gradient(135deg,#fde68a,#fca5a5)" },
  { id: "moon", label: "Moon", css: "grayscale(1) contrast(1.1) brightness(1.1)", swatch: "linear-gradient(135deg,#e5e7eb,#6b7280)" },
  { id: "lark", label: "Lark", css: "saturate(1.3) contrast(0.9) brightness(1.1)", swatch: "linear-gradient(135deg,#bbf7d0,#86efac)" },
  { id: "reyes", label: "Reyes", css: "sepia(0.35) brightness(1.1) contrast(0.85)", swatch: "linear-gradient(135deg,#fed7aa,#fdba74)" },
  { id: "juno", label: "Juno", css: "saturate(1.4) contrast(1.15) hue-rotate(-5deg)", swatch: "linear-gradient(135deg,#fca5a5,#f97316)" },
  { id: "slate", label: "Slate", css: "contrast(1.1) saturate(0.6) brightness(0.95)", swatch: "linear-gradient(135deg,#94a3b8,#334155)" },
];

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

// Blur is a low-frequency operation — running it at a fraction of the
// camera's resolution and scaling the (soft, detail-free) result back
// up looks identical to blurring at full res, but costs roughly
// (downscale)^2 less: at 0.5 that's ~4x fewer pixels to filter.
const BLUR_DOWNSCALE = 0.5;

const supportsVideoFrameCallback =
  typeof HTMLVideoElement !== "undefined" && "requestVideoFrameCallback" in HTMLVideoElement.prototype;

/**
 * Acquires the camera itself (instead of letting LiveKit's
 * setCameraEnabled grab + publish it directly), runs each frame through
 * an offscreen canvas pipeline (color filter + skin-smoothing beautify),
 * and hands back a processed MediaStreamTrack to publish to LiveKit in
 * its place.
 *
 * Beautify is a whole-frame blur blend (frequency-separation
 * approximation): a blurred copy of the frame is blended back over the
 * sharp frame at partial opacity. Blurring washes out fine texture
 * (skin blemishes/noise) far more than it washes out strong edges
 * (eyes, brows, hairline), so blending at low alpha smooths skin while
 * keeping features legible — the same trick most lightweight "beauty
 * mode" camera apps use. No face-landmark model is involved — this is
 * the FaceMasker-free variant, so smoothing always applies evenly
 * across the whole frame rather than being confined to skin only.
 *
 * Performance/sync notes:
 *  - The draw loop is driven by video.requestVideoFrameCallback when
 *    available (only fires for genuinely new camera frames) instead of
 *    a blind requestAnimationFrame tied to display refresh.
 *  - The output canvas is captured in ON-DEMAND mode
 *    (captureStream(0)) and a frame is explicitly requested right
 *    after each real draw, so captured video timestamps line up with
 *    actual processing instead of drifting on an independent timer —
 *    this is what keeps video in sync with the (untouched, directly
 *    published) mic audio.
 *  - The blur pass runs at half resolution and is scaled back up —
 *    pure CPU/GPU savings with no visible quality cost, since blur is
 *    already a low-frequency operation.
 *
 * Because GoLiveControlPanel's client-side recorder
 * (startClientRecording) just reads whatever track ends up published on
 * the room, filters/beautify applied here show up in both the live
 * broadcast AND the saved replay automatically — no changes needed
 * there.
 */
export function useBeautifyCamera({ fps = 30 } = {}) {
  const rawVideoRef = useRef(
    typeof document !== "undefined" ? document.createElement("video") : null
  ); // decode target only — never mounted

  // Created eagerly and never torn down/recreated — start() can be
  // called before any consuming component has mounted a place to
  // display it. A detached canvas still draws and still supports
  // captureStream() fine; the consumer appends canvasRef.current into
  // the DOM wherever it wants the preview to show up.
  const canvasRef = useRef(
    typeof document !== "undefined" ? document.createElement("canvas") : null
  );
  const ctxRef = useRef(canvasRef.current ? canvasRef.current.getContext("2d", { alpha: false }) : null);

  // Scratch canvas, created once and reused every frame.
  const blurCanvasRef = useRef(
    typeof document !== "undefined" ? document.createElement("canvas") : null
  ); // small (downscaled) buffer for the blur pass
  const blurCtxRef = useRef(blurCanvasRef.current ? blurCanvasRef.current.getContext("2d") : null);

  const rafRef = useRef(null); // holds either a rVFC handle or a rAF handle, per supportsVideoFrameCallback
  const rawStreamRef = useRef(null);
  const videoTrackRef = useRef(null); // the on-demand CanvasCaptureMediaStreamTrack, if supported
  const manualCaptureRef = useRef(false);

  const [filterId, setFilterId] = useState("none");
  const [beautify, setBeautify] = useState(35); // 0-100 skin-smoothing intensity
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [facingMode, setFacingMode] = useState("user"); // 'user' (front) | 'environment' (rear)
  const [switchingCamera, setSwitchingCamera] = useState(false);

  // Read via refs inside the draw loop so changing filter/beautify
  // never has to tear down and restart it.
  const filterIdRef = useRef(filterId);
  const beautifyRef = useRef(beautify);
  const facingModeRef = useRef(facingMode);
  useEffect(() => {
    filterIdRef.current = filterId;
  }, [filterId]);
  useEffect(() => {
    beautifyRef.current = beautify;
  }, [beautify]);
  useEffect(() => {
    facingModeRef.current = facingMode;
  }, [facingMode]);

  const drawFrame = useCallback(() => {
    const video = rawVideoRef.current;
    const canvas = canvasRef.current;

    const scheduleNext = () => {
      if (supportsVideoFrameCallback) {
        rafRef.current = video.requestVideoFrameCallback(drawFrame);
      } else {
        rafRef.current = requestAnimationFrame(drawFrame);
      }
    };

    if (!video || !canvas || video.readyState < 2) {
      scheduleNext();
      return;
    }

    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) {
      scheduleNext();
      return;
    }
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }

    const ctx = ctxRef.current;
    const preset = FILTER_PRESETS.find((f) => f.id === filterIdRef.current) || FILTER_PRESETS[0];

    // 1) Base pass — color grade only, no smoothing yet.
    ctx.filter = preset.css;
    ctx.drawImage(video, 0, 0, w, h);

    // 2) Beautify — whole-frame blur blend. Blends at a moderate cap
    // since (with no face mask to confine it) this touches the entire
    // frame, not just skin.
    const intensity = clamp(beautifyRef.current, 0, 100) / 100;
    if (intensity > 0) {
      const bw = Math.max(1, Math.round(w * BLUR_DOWNSCALE));
      const bh = Math.max(1, Math.round(h * BLUR_DOWNSCALE));
      const blurCanvas = blurCanvasRef.current;
      if (blurCanvas.width !== bw || blurCanvas.height !== bh) {
        blurCanvas.width = bw;
        blurCanvas.height = bh;
      }
      const bctx = blurCtxRef.current;
      bctx.filter = `${preset.css === "none" ? "" : preset.css + " "}blur(${(intensity * 6 * BLUR_DOWNSCALE).toFixed(1)}px)`;
      bctx.drawImage(video, 0, 0, bw, bh);

      ctx.save();
      ctx.globalAlpha = intensity * 0.55;
      ctx.drawImage(blurCanvas, 0, 0, bw, bh, 0, 0, w, h); // single call handles the upscale
      ctx.restore();

      // Recover perceived sharpness the blur blend cost us, nudging
      // contrast back up proportional to intensity.
      ctx.save();
      ctx.globalCompositeOperation = "source-atop";
      ctx.filter = `contrast(${(1 + intensity * 0.08).toFixed(2)})`;
      ctx.drawImage(canvas, 0, 0, w, h);
      ctx.restore();
    }

    // Push exactly this frame downstream now, rather than waiting on
    // captureStream's own internal timer — see the on-demand capture
    // note in start(). This is what keeps video timestamps aligned
    // with when frames actually finish processing.
    if (manualCaptureRef.current) {
      videoTrackRef.current?.requestFrame();
    }

    scheduleNext();
  }, []);

  // Starts the camera + draw loop and returns the processed video track
  // to publish. Audio is intentionally not requested here — keep using
  // room.localParticipant.setMicrophoneEnabled(true) for that, unchanged.
  const start = useCallback(async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: { ideal: facingModeRef.current },
        },
        audio: false,
      });
      rawStreamRef.current = stream;

      const video = rawVideoRef.current;
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      video.setAttribute("playsinline", ""); // belt-and-suspenders for iOS Safari

      // A <video> element that's never actually in the document gets
      // its decoding throttled or paused by the browser even though
      // nothing here intended to show it. Keeping it in the DOM (just
      // invisible) keeps frames flowing.
      if (!video.isConnected) {
        video.style.position = "fixed";
        video.style.top = "0";
        video.style.left = "0";
        video.style.width = "1px";
        video.style.height = "1px";
        video.style.opacity = "0";
        video.style.pointerEvents = "none";
        document.body.appendChild(video);
      }

      await video.play();
      setReady(true);

      // Fills whatever container it later gets appended into — the
      // consumer controls positioning/mirroring on that container.
      const canvasEl = canvasRef.current;
      canvasEl.style.width = "100%";
      canvasEl.style.height = "100%";
      canvasEl.style.display = "block";

      // ON-DEMAND capture (fps: 0) — the canvas emits a frame only
      // when we explicitly call track.requestFrame(), which drawFrame
      // does right after finishing each real draw. That ties captured
      // frame timestamps to actual processing instead of an
      // independent timer, which is what keeps this video track in
      // sync with the mic audio track (published separately, untouched,
      // by LiveKit). Falls back to timer-based capture on browsers
      // that accept captureStream(0) but don't implement requestFrame.
      let canvasStream;
      try {
        canvasStream = canvasEl.captureStream(0);
        const track = canvasStream.getVideoTracks()[0];
        if (track && typeof track.requestFrame === "function") {
          manualCaptureRef.current = true;
          videoTrackRef.current = track;
        } else {
          canvasStream = canvasEl.captureStream(fps);
          manualCaptureRef.current = false;
          videoTrackRef.current = null;
        }
      } catch (err) {
        canvasStream = canvasEl.captureStream(fps);
        manualCaptureRef.current = false;
        videoTrackRef.current = null;
      }

      rafRef.current = supportsVideoFrameCallback
        ? video.requestVideoFrameCallback(drawFrame)
        : requestAnimationFrame(drawFrame);

      return canvasStream.getVideoTracks()[0];
    } catch (err) {
      console.error("Beautify camera pipeline failed to start:", err);
      setError("Couldn't access your camera for the filter preview.");
      setReady(false);
      throw err;
    }
  }, [drawFrame, fps]);

  // Swaps front/rear camera. Deliberately does NOT touch the canvas,
  // its captured track, or anything published to LiveKit — only the
  // raw getUserMedia stream feeding rawVideoRef changes. The draw loop
  // just keeps reading whatever video.srcObject currently is, so
  // viewers/the recording see a clean cut with no republish, no
  // renegotiation, and no gap in the broadcast.
  const switchCamera = useCallback(async () => {
    if (!rawStreamRef.current) return; // not started yet — nothing to swap
    setSwitchingCamera(true);
    setError("");
    const nextMode = facingModeRef.current === "user" ? "environment" : "user";
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: { ideal: nextMode },
        },
        audio: false,
      });

      // Swap srcObject and start the new stream playing BEFORE
      // stopping the old one's tracks, so there's no frame where the
      // draw loop has nothing valid to read from.
      const video = rawVideoRef.current;
      const oldStream = rawStreamRef.current;
      video.srcObject = newStream;
      await video.play();
      rawStreamRef.current = newStream;
      oldStream.getTracks().forEach((t) => t.stop());

      setFacingMode(nextMode);
    } catch (err) {
      console.error("Failed to switch camera:", err);
      setError("Couldn't switch cameras — this device may only have one.");
    } finally {
      setSwitchingCamera(false);
    }
  }, []);

  const stop = useCallback(() => {
    if (rafRef.current) {
      if (supportsVideoFrameCallback && rawVideoRef.current?.cancelVideoFrameCallback) {
        rawVideoRef.current.cancelVideoFrameCallback(rafRef.current);
      } else {
        cancelAnimationFrame(rafRef.current);
      }
    }
    rafRef.current = null;
    rawStreamRef.current?.getTracks().forEach((t) => t.stop());
    rawStreamRef.current = null;
    videoTrackRef.current = null;
    manualCaptureRef.current = false;
    if (rawVideoRef.current?.isConnected) {
      rawVideoRef.current.remove();
    }
    setReady(false);
  }, []);

  useEffect(() => () => stop(), [stop]);

  return {
    canvasRef,
    start,
    stop,
    ready,
    error,
    filterId,
    setFilterId,
    beautify,
    setBeautify,
    facingMode,
    switchCamera,
    switchingCamera,
  };
}
